import React, { useState, useEffect, useRef } from "react";
import "./ProductList.css";
import { FaWhatsapp, FaInstagram, FaShareAlt } from "react-icons/fa";
import { Link } from "react-router-dom";
import { io } from "socket.io-client";

const API = process.env.REACT_APP_API_URL || "https://api.manavastralu.com";

// ── Stock — the SAME rule as the backend (backend/services/stock.js) ──
// soldOut flag → stock of the offered sizes (when per-size stock exists) → stock;
// all colour variants 0 → sold out. Missing numbers count as 0, never "in stock".
const toNum = (v) => {
  if (v === null || v === undefined || v === "" || typeof v === "boolean") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};
const keyOf = (s) => String(s || "").trim().toLowerCase();
const sizeMap = (p) => {
  const ss = p && p.sizeStock instanceof Map ? Object.fromEntries(p.sizeStock) : p && p.sizeStock;
  return ss && typeof ss === "object" && !Array.isArray(ss) ? ss : {};
};
const offeredSizes = (p) => (Array.isArray(p?.sizes) ? p.sizes.map((s) => String(s).trim()).filter(Boolean) : []);

// stock of one size ("3xl" == "3XL"); null when that size has no entry
function sizeQty(p, size) {
  const ss = sizeMap(p);
  const k = Object.keys(ss).find((x) => keyOf(x) === keyOf(size));
  return k === undefined ? null : toNum(ss[k]);
}

// how many of this product can be bought in total
function availableStock(p) {
  if (!p || p.soldOut === true) return 0;
  const sizes = offeredSizes(p);
  const hasSizeStock = sizes.length > 0 && Object.keys(sizeMap(p)).length > 0;
  let n = hasSizeStock
    ? sizes.reduce((s, size) => s + Math.max(0, sizeQty(p, size) || 0), 0)
    : Math.max(0, toNum(p.stock) || 0);
  const variants = (Array.isArray(p.colorVariants) ? p.colorVariants : []).filter((v) => v && v.name);
  if (variants.length && variants.every((v) => (toNum(v.stock) || 0) <= 0)) n = 0;
  return n;
}

const isSoldOutItem = (p) => availableStock(p) <= 0;

// how many can be bought in one size (or colour)
function availableFor(p, { size = "", color = "" } = {}) {
  const total = availableStock(p);
  if (total <= 0) return 0;
  const sizes = offeredSizes(p);
  if (size && sizes.length && Object.keys(sizeMap(p)).length) {
    const offered = sizes.some((s) => keyOf(s) === keyOf(size));
    const q = sizeQty(p, size);
    return offered && q !== null ? Math.max(0, q) : 0;
  }
  if (color && Array.isArray(p.colorVariants) && p.colorVariants.length) {
    const v = p.colorVariants.find((x) => keyOf(x?.name) === keyOf(color));
    if (v) return Math.min(total, Math.max(0, toNum(v.stock) || 0));
  }
  return total;
}

// stock of one size — no more "split estimate" guesses
function getStockForSize(item, size) {
  if (!size) return 0;
  return availableFor(item, { size });
}

export default function ProductList({
  columns, sortType, filters,
  wishlist, setWishlist,
  cartItems, setCartItems,
}) {
  const [products,     setProducts]     = useState([]);
  const [shareOpen,    setShareOpen]    = useState(null);
  const [selectedSize, setSelectedSize] = useState({});
  const [loading,      setLoading]      = useState(true);
  const [sizeAlert,    setSizeAlert]    = useState({});
  const [retryCount,   setRetryCount]   = useState(0);
  const [loadError,    setLoadError]    = useState(false);
  const socketRef = useRef(null);

  // ── Fetch with retry + auto-refresh every 60s + live socket push ──
  useEffect(() => {
    let cancelled   = false;
    let retries     = 0;
    const maxRetries = 3;

    const fetchProducts = async (isRefresh = false) => {
      try {
        if (!isRefresh) {
          setLoading(true);
          setLoadError(false);
        }

        const controller = new AbortController();
        const timeout    = setTimeout(() => controller.abort(), 20000);

        const res = await fetch(`${API}/api/products`, {
          signal: controller.signal,
        });
        clearTimeout(timeout);

        if (cancelled) return;
        if (!res.ok) throw new Error(`HTTP ${res.status}`);

        const data = await res.json();

        // ✅ Always update products — keeps sold-out status fresh
        setProducts(data);
        if (!isRefresh) setLoading(false);

      } catch (err) {
        if (cancelled) return;
        if (!isRefresh && retries < maxRetries) {
          retries++;
          setRetryCount(retries);
          setTimeout(() => fetchProducts(false), 2000 * retries);
        } else if (!isRefresh) {
          console.error("Failed to load products:", err.message);
          setLoading(false);
          setLoadError(true);
        }
      }
    };

    fetchProducts(false);

    // ══════════════════════════════════════════════════
    //  LIVE PUSH — instant stock/soldOut/price updates the
    //  moment they change anywhere else (a sale, an admin
    //  edit, a delete). This is what makes it feel like a
    //  real ecommerce site instead of "check back later."
    //  Matches the "stockUpdate" event your backend already
    //  emits via emitStockUpdate() in server.js / socket.js.
    // ══════════════════════════════════════════════════
    const socket = io(API, {
      transports: ["websocket", "polling"],
      reconnection: true,
      reconnectionDelay: 2000,
    });
    socketRef.current = socket;

    socket.on("stockUpdate", (updated) => {
      if (cancelled || !updated?._id) return;
      setProducts(prev => {
        const idx = prev.findIndex(p => (p._id || p.id) === updated._id);
        if (idx === -1) {
          // New product created elsewhere — show it immediately
          return [updated, ...prev];
        }
        const next = [...prev];
        next[idx] = { ...next[idx], ...updated };
        return next;
      });
    });

    // ✅ Refresh every 60 seconds — fallback safety net in case the
    // socket connection drops, keeps sold-out/stock status current
    // across all browser sessions without a manual reload
    const interval = setInterval(() => {
      if (!cancelled) fetchProducts(true);
    }, 60000);

    // Refetch immediately when the tab regains focus — covers a
    // phone/laptop that was asleep and missed both the socket and poll
    const onVisible = () => {
      if (document.visibilityState === "visible" && !cancelled) fetchProducts(true);
    };
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      cancelled = true;
      clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisible);
      socket.disconnect();
    };
  }, []);

  // ── Filtering ──────────────────────────────────────
  let filtered = [...products];
  if (filters?.fabric?.length > 0)
    filtered = filtered.filter(p => filters.fabric.includes(p.fabric));
  if (filters?.color?.length > 0)
    filtered = filtered.filter(p => filters.color.includes(p.color));
  if (filters?.price?.length > 0)
    filtered = filtered.filter(p =>
      filters.price.some(r => {
        if (r === "under1000") return p.price < 1000;
        if (r === "1000-3000") return p.price >= 1000 && p.price <= 3000;
        if (r === "3000-6000") return p.price >= 3000 && p.price <= 6000;
        if (r === "above6000") return p.price > 6000;
        return true;
      })
    );

  // ── Sorting ────────────────────────────────────────
  const sorted = [...filtered];
  if (sortType === "low")  sorted.sort((a, b) => a.price - b.price);
  if (sortType === "high") sorted.sort((a, b) => b.price - a.price);

  // ── Wishlist ───────────────────────────────────────
  const toggleLike = (item) => {
    const id     = item._id || item.id;
    const exists = wishlist.some(p => (p._id || p.id) === id);
    setWishlist(exists
      ? wishlist.filter(p => (p._id || p.id) !== id)
      : [...wishlist, item]
    );
  };

  // ── Image URL ──────────────────────────────────────
  const getImg = (item) => {
    const img = item.images?.[0] || item.image || "";
    if (!img)                       return "/images/s1.png";
    if (img.startsWith("http"))     return img;
    if (img.startsWith("uploads/")) return `${API}/${img}`;
    return `${API}/uploads/${img}`;
  };

  // ── Add to cart ────────────────────────────────────
  // ✅ Now size-aware: when the product has a sizeStock entry for
  // the chosen size, that size's own count is the ceiling (and the
  // "already in cart" count only looks at THIS size, not every
  // size pooled together). Falls back to the old pooled-stock
  // behavior for any product without sizeStock populated.
  const addToCart = (item) => {
    // ✅ Always check DB stock first — never add if sold out
    if (isSoldOutItem(item)) return;

    const itemId   = item._id || item.id;
    const size     = selectedSize[itemId] || "";
    const maxStock = getStockForSize(item, size);

    if (size && maxStock === 0) return; // this specific size is sold out

    // ✅ FIX — size is required whenever the product HAS sizes
    if (item.sizes?.length > 0 && !size) {
      setSizeAlert(prev => ({ ...prev, [itemId]: true }));
      setTimeout(() => setSizeAlert(prev => ({ ...prev, [itemId]: false })), 3000);
      return;
    }

    // Check quantity already in cart for this specific size
    const totalInCart = cartItems
      .filter(i => (i._id || i.id) === itemId && (!size || i.selectedSize === size))
      .reduce((sum, i) => sum + (i.quantity || 1), 0);

    if (totalInCart >= maxStock) {
      alert(`Only ${maxStock} pieces available${size ? ` in size ${size}` : ""}. You already have ${totalInCart} in cart.`);
      return;
    }

    setSizeAlert(prev => ({ ...prev, [itemId]: false }));

    const color = item.color || "";
    const cartKey = `${itemId}_${size}_${color}`;
    const cartItem = {
      ...item,
      selectedSize: size,
      quantity:     1,
      stock:        maxStock,
      image:        getImg(item),
    };

    // Merge if same product+size+color already in cart
    if (cartItems.some(p => `${p._id||p.id}_${p.selectedSize}_${p.color||""}` === cartKey)) {
      setCartItems(cartItems.map(i =>
        `${i._id||i.id}_${i.selectedSize}_${i.color||""}` === cartKey
          ? { ...i, quantity: (i.quantity||1) + 1 }
          : i
      ));
    } else {
      setCartItems([...cartItems, cartItem]);
    }

    if (window.showCartToast) window.showCartToast(cartItem);
  };

  // ── Share ──────────────────────────────────────────
  const share = (platform, item) => {
    const url  = `${window.location.origin}/product/${item._id||item.id}`;
    const text = `Check out: ${item.name} \u20B9${item.price}`;
    if (platform === "whatsapp")
      window.open(`https://wa.me/?text=${encodeURIComponent(text+" "+url)}`, "_blank");
    if (platform === "instagram")
      window.open("https://www.instagram.com/", "_blank");
  };

  // ── SKELETON LOADER ────────────────────────────────
  if (loading) return (
    <div style={{ background:"#fdf8f2", minHeight:400, padding:"40px 24px" }}>
      <style>{`
        @keyframes shimmer {
          0%   { background-position: 200% 0; }
          100% { background-position:-200% 0; }
        }
        @keyframes pulse {
          0%,100%{ opacity:1; }
          50%    { opacity:0.5; }
        }
        .skel-bar {
          background: linear-gradient(90deg,#f5e8d8 25%,#fdf0e8 50%,#f5e8d8 75%);
          background-size: 200% 100%;
          animation: shimmer 1.8s infinite;
          border-radius: 4px;
        }
      `}</style>

      <div style={{ textAlign:"center", marginBottom:32 }}>
        <div style={{ fontSize:36, marginBottom:10 }}>🥻</div>
        <p style={{
          color:"#8b4513", fontSize:15, fontWeight:600,
          fontFamily:"Georgia,serif", margin:"0 0 4px",
        }}>
          Loading our collection…
        </p>
        <p style={{ color:"#b08060", fontSize:12, margin:0 }}>
          {retryCount > 0
            ? `Retrying… (attempt ${retryCount}/3)`
            : "Please wait a moment"}
        </p>
        <div style={{
          width:180, height:3, background:"#f0e0cc",
          borderRadius:2, margin:"14px auto 0", overflow:"hidden",
        }}>
          <div className="skel-bar" style={{ height:"100%", width:"100%" }}/>
        </div>
      </div>

      <div style={{
        display:"grid",
        gridTemplateColumns:"repeat(auto-fill,minmax(220px,1fr))",
        gap:20, maxWidth:1280, margin:"0 auto",
      }}>
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} style={{
            background:"#fff", borderRadius:8,
            border:"1px solid #f0e0cc", overflow:"hidden",
            animation:`pulse 1.6s ease infinite`,
            animationDelay:`${i * 0.15}s`,
          }}>
            <div className="skel-bar" style={{ height:240 }}/>
            <div style={{ padding:"14px 16px" }}>
              <div className="skel-bar" style={{ height:14, width:"80%", marginBottom:8 }}/>
              <div className="skel-bar" style={{ height:11, width:"55%", marginBottom:12 }}/>
              <div className="skel-bar" style={{ height:18, width:"40%", marginBottom:14 }}/>
              <div className="skel-bar" style={{ height:36, width:"100%", borderRadius:4 }}/>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  // ── ERROR STATE ────────────────────────────────────
  if (loadError) return (
    <div style={{ textAlign:"center", padding:"60px 24px", fontFamily:"Georgia,serif" }}>
      <div style={{ fontSize:48, marginBottom:16 }}>😔</div>
      <h3 style={{ color:"#8b4513", fontSize:18, marginBottom:8 }}>
        Could not load products
      </h3>
      <p style={{ color:"#b08060", fontSize:13, marginBottom:24 }}>
        The server may be waking up. Please try again.
      </p>
      <button
        onClick={() => {
          setLoadError(false);
          setLoading(true);
          setRetryCount(0);
          fetch(`${API}/api/products`)
            .then(r => r.json())
            .then(data => { setProducts(data); setLoading(false); })
            .catch(() => { setLoading(false); setLoadError(true); });
        }}
        style={{
          background:"#8b4513", color:"#fff", border:"none",
          padding:"12px 32px", fontSize:13, fontWeight:700,
          letterSpacing:"1px", cursor:"pointer", borderRadius:6,
        }}>
        🔄 Try Again
      </button>
    </div>
  );

  // ── EMPTY ──────────────────────────────────────────
  if (sorted.length === 0) return (
    <div style={{
      textAlign:"center", padding:"60px 0",
      fontFamily:"Georgia,serif", color:"#8b4513", fontSize:16,
    }}>
      No products found.
    </div>
  );

  // ── PRODUCT GRID ───────────────────────────────────
  return (
    <>
      <style>{`
        @keyframes shake {
          0%,100%{ transform:translateX(0); }
          20%    { transform:translateX(-4px); }
          40%    { transform:translateX(4px); }
          60%    { transform:translateX(-3px); }
          80%    { transform:translateX(3px); }
        }
        .size-alert {
          display:flex; align-items:center; gap:6px;
          background:#fff3cd; border:1px solid #ffc107;
          color:#856404; border-radius:5px;
          padding:7px 10px; font-size:12px; font-weight:600;
          margin-bottom:6px; animation:shake .4s ease;
        }
        .size-pill {
          padding:3px 10px; font-size:11px; cursor:pointer;
          border-radius:3px; transition:all .15s; user-select:none;
        }
        .size-pill:hover { border-color:#8b4513!important; }
        .size-pill.selected {
          border:1.5px solid #8b4513!important;
          color:#8b4513!important;
          background:#fdf6ee!important;
          font-weight:700!important;
        }
        .size-pill.unselected {
          border:1px solid #d4b896;
          color:#9a7050;
          background:#fff;
        }
        .cart-btn-custom {
          width:100%; padding:10px 0; font-size:12px; font-weight:700;
          letter-spacing:.8px; border:none; cursor:pointer;
          transition:all .2s; border-radius:4px;
        }
        .cart-btn-custom:hover:not(:disabled) {
          opacity:.88; transform:translateY(-1px);
        }
        .cart-btn-custom:disabled { cursor:not-allowed; }
      `}</style>

      <div className={`product-section cols-${columns}`}>
        {sorted.map((item, index) => {
          const itemId   = item._id || item.id;
          const isWished = wishlist.some(p => (p._id||p.id) === itemId);

          // ✅ isSoldOut comes from DB data — always takes priority
          const available = availableStock(item);
          const isSoldOut = available <= 0;

          const discount = item.originalPrice > item.price
            ? Math.round(((item.originalPrice - item.price) / item.originalPrice) * 100)
            : null;

          const showSizeAlert = sizeAlert[itemId];
          const hasSizes      = item.sizes?.length > 0;
          // ✅ FIX — size is required for ANY product with sizes defined,
          // not just ones whose name/category/fabric happens to contain
          // the literal word "blouse".
          const sizeRequired  = hasSizes;
          const sizeChosen    = !!selectedSize[itemId];
          // ✅ NEW — used only to hide the size selector on blouse cards
          // (per request: blouse size isn't shown on the home page grid).
          const isBlouseItem  = /blouse/i.test(`${item.name} ${item.category||""} ${item.fabric||""}`);

          // ✅ Size-aware cart/stock state
          const chosenSize    = selectedSize[itemId] || "";
          const effectiveMax  = chosenSize ? getStockForSize(item, chosenSize) : available;

          const totalInCart = cartItems
            .filter(i => (i._id||i.id) === itemId && (!chosenSize || i.selectedSize === chosenSize))
            .reduce((sum, i) => sum + (i.quantity||1), 0);
          const inCart    = !isSoldOut && totalInCart > 0;
          const stockFull = !isSoldOut && totalInCart >= effectiveMax;

          // ✅ Button text — isSoldOut always wins first
          const btnText = isSoldOut                                    ? "SOLD OUT"
                        : stockFull                                    ? `MAX ${effectiveMax} IN CART`
                        : inCart                                       ? "✓ ADD MORE"
                        : sizeRequired && !sizeChosen                  ? "SELECT SIZE & ADD"
                        : "ADD TO CART";

          const btnBg   = isSoldOut  ? "#999"
                        : stockFull  ? "#c62828"
                        : inCart     ? "#5c3317"
                        : "";

          return (
            <div className="card" key={itemId || index}>

              {/* IMAGE — Link stays clickable even when sold out */}
              <div className="img-box" style={{ position:"relative" }}>
                <Link to={`/product/${itemId}`}>
                  <img src={getImg(item)} alt={item.name}
                    // first row loads at once; the rest only when scrolled near
                    loading={index < 4 ? "eager" : "lazy"}
                    fetchpriority={index < 4 ? "high" : "low"}
                    decoding="async"
                    onError={e => { if (!e.target.dataset.fallback) { e.target.dataset.fallback = "1"; e.target.src = "/images/s1.png"; } }}
                    style={{ cursor:"pointer" }}/>
                </Link>

                {/* ✅ Sold out overlay — pointerEvents:"none" lets clicks
                    pass through to the <Link>, so users can still open
                    the Product Details page for sold-out items */}
                {isSoldOut && (
                  <div style={{
                    position:"absolute", top:0, left:0, right:0, bottom:0,
                    background:"rgba(0,0,0,0.45)",
                    display:"flex", alignItems:"center", justifyContent:"center",
                    zIndex:4,
                    pointerEvents:"none",
                  }}>
                    <span style={{
                      background:"#c62828", color:"#fff",
                      padding:"8px 20px", fontWeight:700,
                      fontSize:13, letterSpacing:"1.5px",
                    }}>
                      SOLD OUT
                    </span>
                  </div>
                )}

                {/* ✅ In cart badge — only shown when NOT sold out */}
                {!isSoldOut && inCart && (
                  <div style={{
                    position:"absolute", top:0, left:0, right:0,
                    background:"rgba(92,51,23,0.85)",
                    color:"#fff", fontSize:11, fontWeight:700,
                    textAlign:"center", padding:"5px 0",
                    letterSpacing:"0.8px", zIndex:3,
                    pointerEvents:"none",
                  }}>
                    ✓ IN YOUR CART ({totalInCart}/{effectiveMax})
                  </div>
                )}

                {/* Low stock badge */}
                {!isSoldOut && !inCart && available > 0 && available <= 3 && (
                  <div style={{
                    position:"absolute", bottom:8, left:8,
                    background:"#ff6f00", color:"#fff",
                    fontSize:10, fontWeight:700,
                    padding:"3px 8px", letterSpacing:"0.5px", zIndex:2,
                    pointerEvents:"none",
                  }}>
                    Only {available} left!
                  </div>
                )}

                {discount && !isSoldOut && (
                  <span className="discount" style={{ pointerEvents:"none" }}>
                    {discount}% OFF
                  </span>
                )}

                <div
                  className={`heart ${isWished ? "active" : ""}`}
                  onClick={() => toggleLike(item)}
                  style={{ zIndex:5 }}>
                  ♥
                </div>
              </div>

              {/* NAME — ✅ also opens Product Details (works when sold out) */}
              <Link to={`/product/${itemId}`}
                style={{ textDecoration:"none", color:"inherit" }}>
                <h3 style={{ cursor:"pointer" }}>{item.name}</h3>
              </Link>

              {/* FABRIC & COLOR */}
              {(item.fabric || item.color) && (
                <p style={{
                  fontSize:11, color:"#9a7050",
                  margin:"2px 0 6px",
                  textTransform:"uppercase", letterSpacing:"0.5px",
                }}>
                  {item.fabric}{item.color ? ` · ${item.color}` : ""}
                </p>
              )}

              {/* SIZE ALERT — ✅ hidden for blouses per request (no size
                  selector below to act on, so no point alerting) */}
              {showSizeAlert && sizeRequired && !isSoldOut && !isBlouseItem && (
                <div className="size-alert">
                  <span>⚠️</span>
                  Please select a blouse size before adding to cart
                </div>
              )}

              {/* SIZE SELECTOR — ✅ hidden for blouses on the home page
                  grid per request. Any other sized (non-blouse) product
                  still shows its selector exactly as before. Because
                  this whole block simply doesn't render for blouses,
                  there's no leftover blank gap where it used to sit. */}
              {hasSizes && !isSoldOut && !isBlouseItem && (
                <div style={{ display:"flex", gap:4, flexWrap:"wrap", marginBottom:6 }}>
                  {item.sizes.map(size => {
                    const thisSizeStock = getStockForSize(item, size);
                    const thisSizeSoldOut = thisSizeStock === 0;
                    return (
                      <span
                        key={size}
                        className={`size-pill ${selectedSize[itemId] === size ? "selected" : "unselected"}`}
                        onClick={() => {
                          if (thisSizeSoldOut) return;
                          setSelectedSize({ ...selectedSize, [itemId]: size });
                          setSizeAlert(prev => ({ ...prev, [itemId]: false }));
                        }}
                        style={thisSizeSoldOut ? { opacity:.4, textDecoration:"line-through", cursor:"not-allowed" } : undefined}
                        title={thisSizeSoldOut ? "Sold out in this size" : undefined}>
                        {size}
                      </span>
                    );
                  })}
                </div>
              )}

              {/* Stock progress bar — only when in cart and not sold out */}
              {!isSoldOut && totalInCart > 0 && (
                <div style={{ marginBottom:6 }}>
                  <div style={{
                    display:"flex", justifyContent:"space-between",
                    fontSize:10, color:"#9a7050", marginBottom:2,
                  }}>
                    <span>{totalInCart} in cart</span>
                    <span>{effectiveMax - totalInCart} more available</span>
                  </div>
                  <div style={{ height:3, background:"#f0e8e0", borderRadius:2 }}>
                    <div style={{
                      height:"100%", borderRadius:2,
                      width:`${Math.min((totalInCart/(effectiveMax||1))*100,100)}%`,
                      background: stockFull ? "#c62828" : "#8b4513",
                      transition:"width .3s",
                    }}/>
                  </div>
                </div>
              )}

              {/* PRICE */}
              <p className="price">
                ₹{Number(item.price).toLocaleString()}
                {item.originalPrice > item.price && (
                  <span className="old"> ₹{Number(item.originalPrice).toLocaleString()}</span>
                )}
                <span className="share-btn"
                  onClick={() => setShareOpen(shareOpen === itemId ? null : itemId)}>
                  <FaShareAlt/>
                </span>
              </p>

              {/* SHARE POPUP */}
              {shareOpen === itemId && (
                <div className="share-popup">
                  <FaWhatsapp onClick={() => share("whatsapp", item)}/>
                  <FaInstagram onClick={() => share("instagram", item)}/>
                </div>
              )}

              {/* ✅ ADD TO CART button removed per request. The
                  addToCart function, cart-badge, "in cart" progress
                  bar, and stockFull/btnText/btnBg logic above are
                  left as-is (now just unused where they only fed
                  this button) in case you want it back later. */}

            </div>
          );
        })}
      </div>
    </>
  );
}